import { Box, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const QUOTE_KEYS = [
  "login.quotes.smallTouches",
  "login.quotes.smartMoves",
  "login.quotes.everyGuest",
  "login.quotes.coaching",
  "login.quotes.heart",
  "login.quotes.greatService",
  "login.quotes.details",
  "login.quotes.betterData",
  "login.quotes.measure",
  "login.quotes.memorable",
  "login.quotes.teamwork",
  "login.quotes.purpose",
  "login.quotes.remarkable",
] as const;

interface AuthPageLayoutProps {
  children: React.ReactNode;
}

const AuthPageLayout: React.FC<AuthPageLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Math.random() * QUOTE_KEYS.length));
  const [showQuote, setShowQuote] = useState(true);

  useEffect(() => {
    let transitionTimeout: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      if (reducedMotion) {
        setQuoteIndex((previous) => (previous + 1) % QUOTE_KEYS.length);
        return;
      }
      setShowQuote(false);
      transitionTimeout = setTimeout(() => {
        setQuoteIndex((previous) => (previous + 1) % QUOTE_KEYS.length);
        setShowQuote(true);
      }, 350);
    }, 5000);

    return () => {
      clearInterval(interval);
      if (transitionTimeout) clearTimeout(transitionTimeout);
    };
  }, [reducedMotion]);

  const quote = (
    <Box
      sx={{
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 1,
      }}
    >
      <Box
        component="p"
        sx={{
          fontSize: { xs: "1rem", md: "1.125rem" },
          opacity: showQuote ? 1 : 0,
          transition: reducedMotion ? "none" : "opacity 300ms ease-in-out",
        }}
      >
        {t(QUOTE_KEYS[quoteIndex])}
      </Box>
    </Box>
  );

  const footer = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        fontSize: "0.75rem",
      }}
    >
      <span>{t("login.text.copyright", { getFullYear: new Date().getFullYear() })}</span>
      <span>{t("login.text.version", { appVersion: APP_VERSION })}</span>
      <a
        href="https://www.craveretail.com/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline focus-visible:underline"
      >
        {t("login.text.privacyPolicy")}
      </a>
    </Box>
  );

  return (
    <Box
      className="auth-page flex flex-col"
      sx={{
        minHeight: "100dvh",
        py: { xs: 2, md: 6 },
        paddingTop: { xs: "max(16px, env(safe-area-inset-top))", md: 6 },
        paddingBottom: { xs: "calc(64px + env(safe-area-inset-bottom))", md: 6 },
        paddingInline: "max(16px, env(safe-area-inset-left), env(safe-area-inset-right))",
      }}
    >
      <Box
        className="bg-white rounded-2xl shadow-2xl overflow-hidden flex w-full max-w-5xl"
        sx={{ flexDirection: { xs: "column", md: "row" }, m: "auto", flexShrink: 0 }}
      >
        <Box
          className="bg-gradient-to-br from-blue-600 to-primary-light text-white relative overflow-hidden"
          sx={{
            p: { xs: 2, md: 6 },
            width: { md: "50%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", my: { xs: 0, md: 2 } }}>
            <Box
              component="img"
              src="/images/Crave Retail Horizontal Stacked - White.png"
              alt={t("login.accessibility.craveRetail")}
              sx={{ width: { xs: 180, md: "100%" }, height: "auto", objectFit: "contain" }}
            />
          </Box>
          <Box sx={{ display: { xs: "none", md: "block" }, mt: 2 }}>{quote}</Box>
          <Box sx={{ display: { xs: "none", md: "block" }, mt: "auto", pt: 3 }}>{footer}</Box>
        </Box>
        <Box
          className="bg-gradient-to-br from-white to-gray-50"
          sx={{ p: { xs: 2, sm: 3, md: 6 }, width: { md: "50%" }, minWidth: 0 }}
        >
          {children}
          <Box sx={{ display: { xs: "block", md: "none" }, mt: 2, color: "text.secondary" }}>
            {quote}
            <Box sx={{ mt: 2 }}>{footer}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthPageLayout;
